import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react';
import { CompleteQuery } from 'modules/queryBuilder/types';
import { noop } from 'utils/noop';
import { PAGE_VARIATIONS } from '../constants';
import { useGetPageVariation, useListDetailsByIdQuery, useListId } from '../hooks';
import { useUpdatedListDetailsSearchQuery } from '../hooks/useUpdatedListDetailsSearchQuery';
import { List } from '../types';

export type ListDetailsState = {
  didError: boolean;
  isLoading: boolean;
  list: List;
  // Search query is only defined for dynamic lists
  searchQuery?: CompleteQuery;
  pageVariation: PAGE_VARIATIONS;
  refetch: () => void;
};

const defaultListDetailsState = {
  didError: false,
  isLoading: false,
  list: {} as List,
  pageVariation: PAGE_VARIATIONS.SEGMENTATION,
  refetch: noop,
};

export const ListDetailsContext = createContext<ListDetailsState>(defaultListDetailsState);
export const useListDetailsContext = () => useContext(ListDetailsContext);

export const ListDetailsContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const listId = useListId();
  const {
    list,
    searchQuery: listSearchQuery,
    isLoading,
    didError,
    refetch,
  } = useListDetailsByIdQuery(listId);
  const { emailListType: listType } = list;
  const pageVariation = useGetPageVariation(listId, listType);
  const searchQuery = useUpdatedListDetailsSearchQuery(listSearchQuery);

  const value = useMemo(
    () => ({
      didError,
      isLoading: isLoading,
      list,
      searchQuery,
      pageVariation,
      refetch,
    }),
    [didError, isLoading, list, listSearchQuery, pageVariation, refetch],
  );

  return <ListDetailsContext.Provider value={value}>{children}</ListDetailsContext.Provider>;
};
